// src/pages/onboarding/PaymentPlans.tsx
import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiChevronLeft } from "react-icons/fi";
import styles from "./PaymentPlans.module.css";
import type { StepProps } from "../../types/onboarding";
import type { Plan, CreateCheckoutSessionResponse } from "../../types/payments";
import {
  createCheckoutSession,
  initializePlan,
  validatePromoCode,
  redeemPromoCode,
  type PromoValidationResult,
} from "../../services/paymentsService";
import NextButton from "../../components/common/NextButton";
import { useOnboarding } from "../../hooks/useOnboarding";
import { useAuth } from "../../contexts/AuthContext";
import onboardingService from "../../services/onboardingService";
import { loadOnboardingData, saveOnboardingData } from "../../utils/storage";

// Cast react-icon to a React component type for TSX
type SvgIcon = React.FC<React.SVGProps<SVGSVGElement>>;
const BackIcon = FiChevronLeft as unknown as SvgIcon;

const planDefinitions: Array<{
  id: Plan;
  title: string;
  price: string;
  period?: string;
  badge?: string;
  description: string;
  features: string[];
  buttonText: string;
  selectable: boolean;
  isContactSales?: boolean;
}> = [
  {
    id: "free",
    title: "Free",
    price: "Free",
    description: "Get started with limited features",
    features: [
      "1 manual send per week",
      "Basic analytics",
      "Email support",
    ],
    buttonText: "Get Started Free",
    selectable: true,
  },
  {
    id: "basic",
    title: "Basic",
    price: "$50",
    period: "/month",
    badge: "POPULAR",
    description: "Unlimited sends and automations",
    features: [
      "Unlimited manual sends",
      "Unlimited automations",
      "Advanced analytics",
      "Priority email support",
    ],
    buttonText: "Start with Basic",
    selectable: true,
  },
  {
    id: "pro",
    title: "Pro",
    price: "$75",
    period: "/month",
    description: "Full access to all features",
    features: [
      "Everything in Basic",
      "Data room access",
      "Deck storage",
      "Advanced integrations",
    ],
    buttonText: "Start with Pro",
    selectable: true,
  },
  {
    id: "enterprise",
    title: "Enterprise",
    price: "Contact Sales",
    description: "Custom solutions for your team",
    features: [
      "Everything in Pro",
      "Dedicated account manager",
      "Custom integrations",
      "SLA guarantees",
    ],
    buttonText: "Contact Sales",
    selectable: true,
    isContactSales: true,
  },
];

const PaymentPlans: React.FC<StepProps> = ({ onNext, onBack }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data } = useOnboarding();
  const [selectedPlan, setSelectedPlan] = useState<Plan>("free");
  const [loadingPlan, setLoadingPlan] = useState<Plan | null>(null);
  const [isSkipping, setIsSkipping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Promo code state
  const [promoCode, setPromoCode] = useState<string>("");
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoResult, setPromoResult] = useState<PromoValidationResult | null>(null);
  const [promoRedeeming, setPromoRedeeming] = useState(false);
  const [showPromoInput, setShowPromoInput] = useState(false);

  const handleValidatePromo = useCallback(async (code: string) => {
    if (!code.trim()) return;
    setPromoValidating(true);
    setPromoResult(null);
    try {
      const result = await validatePromoCode(code.trim());
      setPromoResult(result);
      if (result.valid) {
        setSelectedPlan("pro");
      }
    } catch (err) {
      setPromoResult({ valid: false, error: "Failed to validate promo code" });
    } finally {
      setPromoValidating(false);
    }
  }, []);

  // Auto-apply promo code from localStorage
  useEffect(() => {
    const storedPromo = localStorage.getItem("promo_code");
    if (storedPromo) {
      setPromoCode(storedPromo);
      setShowPromoInput(true);
      handleValidatePromo(storedPromo);
    }
  }, [handleValidatePromo]);

  const handleRedeemPromo = async () => {
    if (!promoCode.trim() || !user) return;
    setPromoRedeeming(true);
    setError(null);
    try {
      const result = await redeemPromoCode(promoCode.trim());
      if (result.success && result.redemption) {
        // Save to onboarding data
        const currentData = loadOnboardingData() || {};
        saveOnboardingData({
          ...currentData,
          payment_plan: result.redemption.plan_tier,
          promo_code: promoCode.trim(),
          promo_redeemed: true,
          payment_skipped: false,
        });
        // Clean up localStorage
        localStorage.removeItem("promo_code");
        // Complete onboarding
        const finalOnboardingData = {
          ...data,
          payment_plan: result.redemption.plan_tier,
          promo_redeemed: true,
        };
        await onboardingService.completeOnboarding(user.id, finalOnboardingData);
        navigate("/onboarding-complete?plan=pro&promo=true", { replace: true });
      } else {
        setError(result.error || "Failed to redeem promo code");
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to redeem promo code";
      setError(message);
    } finally {
      setPromoRedeeming(false);
    }
  };

  const handleSelectPlan = (planId: Plan) => {
    const pd = planDefinitions.find((p) => p.id === planId);
    if (pd && pd.selectable && !loadingPlan) {
      setSelectedPlan(planId);
    }
  };

  const handleContactSales = () => {
    window.location.href = "mailto:sales@sendupdates.ai?subject=Enterprise%20Plan%20Inquiry";
  };

  const handleCheckout = async (planId: Plan) => {
    const plan = planDefinitions.find((p) => p.id === planId);

    // Handle Enterprise (Contact Sales)
    if (plan?.isContactSales) {
      handleContactSales();
      return;
    }

    setLoadingPlan(planId);
    setError(null);
    setSelectedPlan(planId);

    try {
      // If Free plan, skip Stripe and complete onboarding directly
      if (planId === "free") {
        if (!user) {
          throw new Error("User not found. Please log in again.");
        }

        const finalOnboardingData = {
          ...data,
          payment_skipped: false,
          payment_plan: "free",
        };

        // Initialize the plan in usage_tracking for immediate feature access
        try {
          await initializePlan("free");
        } catch (initErr) {
          console.warn("Failed to initialize plan, continuing:", initErr);
        }

        await onboardingService.completeOnboarding(user.id, finalOnboardingData);
        navigate("/onboarding-complete?plan=free", { replace: true });
        return;
      }

      // For paid plans (Basic $50, Pro $75), go through Stripe Checkout
      // Save selected plan to localStorage so OnboardingComplete can use it as fallback
      const currentData = loadOnboardingData() || {};
      saveOnboardingData({ ...currentData, payment_plan: planId });

      const response: CreateCheckoutSessionResponse =
        await createCheckoutSession(planId);
      
      // Use the checkout URL directly instead of redirectToCheckout
      if (response.url) {
        window.location.href = response.url;
      } else {
        throw new Error("No checkout URL returned from server.");
      }
    } catch (err: any) {
      console.error("Payment checkout error", err);
      setError(err?.message || "An unexpected error occurred.");
      setLoadingPlan(null);
    }
  };

  const handleSkip = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to skip payment? You'll have limited functionality until you upgrade."
    );
    if (!confirmed) return;

    if (!user) {
      setError("User not found. Please log in again.");
      return;
    }

    setIsSkipping(true);
    setError(null);

    try {
      // Complete onboarding without payment
      const finalOnboardingData = {
        ...data,
        payment_skipped: true,
        payment_plan: "free",
      };

      // Initialize the plan in usage_tracking for immediate feature access
      try {
        await initializePlan("free");
      } catch (initErr) {
        console.warn("Failed to initialize plan, continuing:", initErr);
      }

      // Use the onboarding service to mark as complete
      await onboardingService.completeOnboarding(user.id, finalOnboardingData);

      // Redirect to onboarding complete page
      navigate("/onboarding-complete", { replace: true });
    } catch (err: any) {
      console.error("Failed to skip payment:", err);
      setError("Failed to complete onboarding. Please try again.");
    } finally {
      setIsSkipping(false);
    }
  };

  return (
    <section className={styles.container}>
      {/* Back button removed per design */}

      <h2 className={styles.heading}>7 Day Free Trial</h2>
      <p className={styles.subheading}>
        Only now, unlock flows for scheduling, adding more integrations, team
        onboarding, and expanding the use case—because users believe.
      </p>

      {error && <p className={styles.errorText}>{error}</p>}

      {promoResult?.valid && (
        <div className={styles.promoSuccess}>
          <p className={styles.promoSuccessText}>
            {(promoResult.plan_tier || "Pro").charAt(0).toUpperCase() + (promoResult.plan_tier || "pro").slice(1)} plan — Free for {promoResult.duration_months} {promoResult.duration_months === 1 ? "month" : "months"}!
          </p>
          <button
            type="button"
            onClick={handleRedeemPromo}
            disabled={promoRedeeming}
            className={styles.promoActivateButton}
          >
            {promoRedeeming ? "Activating..." : `Activate Free ${(promoResult.plan_tier || "Pro").charAt(0).toUpperCase() + (promoResult.plan_tier || "pro").slice(1)} Access`}
          </button>
        </div>
      )}

      <div className={styles.cardsContainer}>
        {planDefinitions.map((plan) => {
            const isSelected = plan.id === selectedPlan;
            const isLoading = loadingPlan === plan.id;
            const otherLoading = loadingPlan !== null && !isLoading;
            return (
              <article
                key={plan.id}
                className={`${styles.card} ${
                  isSelected ? styles.selected : ""
                }`}
                onClick={() => handleSelectPlan(plan.id)}
                role={plan.selectable ? "button" : undefined}
                tabIndex={plan.selectable ? 0 : undefined}
                onKeyPress={
                  plan.selectable
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          handleSelectPlan(plan.id);
                        }
                      }
                    : undefined
                }
              >
                {plan.badge && (
                  <span
                    className={
                      isSelected ? styles.badgeSelected : styles.badge
                    }
                  >
                    {plan.badge}
                  </span>
                )}
                <h3 className={styles.cardTitle}>{plan.title}</h3>
                <div className={styles.priceLine}>
                  <span className={styles.price}>{plan.price}</span>
                  {plan.period && (
                    <span className={styles.period}>{plan.period}</span>
                  )}
                </div>
                <p
                  className={
                    isSelected ? styles.captionSelected : styles.caption
                  }
                >
                  {plan.description}
                </p>
                {/* Feature list */}
                <ul className={styles.featureList}>
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className={styles.featureItem}>
                      <span className={styles.checkmark}>✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
                {plan.selectable && (
                  <button
                    type="button"
                    className={plan.isContactSales ? styles.cardButtonSecondary : styles.cardButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCheckout(plan.id);
                    }}
                    disabled={otherLoading || isSkipping}
                    aria-busy={isLoading}
                  >
                    {isLoading ? "Loading..." : plan.buttonText}
                  </button>
                )}
              </article>
            );
          })}
      </div>

      {/* Promo Code Section */}
      <div className={styles.promoSection}>
        {!showPromoInput ? (
          <button
            type="button"
            className={styles.promoToggle}
            onClick={() => setShowPromoInput(true)}
          >
            Have a promo code?
          </button>
        ) : (
          <div className={styles.promoInputWrapper}>
            <input
              type="text"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              placeholder="Enter promo code"
              className={styles.promoInput}
              disabled={promoValidating || promoRedeeming}
            />
            <button
              type="button"
              onClick={() => handleValidatePromo(promoCode)}
              disabled={promoValidating || !promoCode.trim()}
              className={styles.promoApplyButton}
            >
              {promoValidating ? "Checking..." : "Apply"}
            </button>
          </div>
        )}
        {promoResult && !promoResult.valid && (
          <p className={styles.promoError}>{promoResult.error}</p>
        )}
      </div>

      <div className={styles.navButtons}>
        <NextButton
          onClick={() => handleCheckout(selectedPlan)}
          disabled={loadingPlan !== null || isSkipping || promoRedeeming}
        >
          {loadingPlan ? "Processing..." : "Start Trial"}
        </NextButton>
      </div>
    </section>
  );
};

export default PaymentPlans;
